import React, { useState, useEffect, useRef } from 'react';
import { usePractice } from '../context/PracticeContext';
import { useAuth } from '../auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const PracticeLogo = () => {
  const { practiceId, logoUrl, refreshLogo, clearLogo } = usePractice();
  const { keycloak } = useAuth();
  const authHeaders = () => (
    keycloak?.token ? { Authorization: `Bearer ${keycloak.token}` } : {}
  );
  const [showPopover, setShowPopover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPopover]);

  // Handle file upload (from file picker or paste)
  const uploadFile = async (file) => {
    if (!file || !practiceId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/practices/${practiceId}/logo`, {
        method: 'POST',
        body: formData,
        headers: authHeaders(),
      });
      if (res.ok) {
        refreshLogo();
        setShowPopover(false);
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    // Reset input so re-selecting same file triggers change
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const ext = imageType.split('/')[1] || 'png';
          const file = new File([blob], `logo.${ext}`, { type: imageType });
          uploadFile(file);
          return;
        }
      }
    } catch (err) {
      console.error('Clipboard read failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!practiceId) return;
    try {
      await fetch(`${API_BASE_URL}/practices/${practiceId}/logo`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      clearLogo();
      setShowPopover(false);
    } catch (err) {
      console.error('Logo delete failed:', err);
    }
  };

  if (!practiceId) return null;

  return (
    <div className="practice-logo-container" ref={containerRef}>
      {/* Logo or placeholder */}
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Praxis-Logo"
          className="practice-logo-img"
          onClick={() => setShowPopover((v) => !v)}
        />
      ) : (
        <button
          className="practice-logo-placeholder"
          onClick={() => setShowPopover((v) => !v)}
          title="Praxis-Logo hinzufügen"
          aria-label="Praxis-Logo hinzufügen"
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}

      {/* Popover */}
      {showPopover && (
        <div className="practice-logo-popover">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            className="practice-logo-popover-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Wird hochgeladen...' : 'Bild hochladen'}
          </button>
          <button
            className="practice-logo-popover-btn"
            onClick={handlePaste}
            disabled={uploading}
          >
            Aus Zwischenablage einfügen
          </button>
          {logoUrl && (
            <button
              className="practice-logo-popover-btn practice-logo-popover-btn--danger"
              onClick={handleDelete}
            >
              Logo entfernen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PracticeLogo;
